import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { listRoteiros } from "../../services/roteirosService";
import { updateRoteiroSite } from "../../services/roteirosSiteService";
import { uploadRoteiroImagem, deleteRoteiroImagem } from "../../services/uploadsService";
import { simboloMoeda } from "../../utils/moeda";
import ImageUploadField from "../ui/ImageUploadField";

// Lista todos os Roteiros do guia com campos editáveis do site público:
// toggle ExibirNoSite + PrecoApartirDe + DescricaoCurta PT/IT + ImagemUrl.
// PATCH /api/Roteiros/{id}/site dedicado, não toca nos outros campos.
// PR4 moeda-por-guia: moeda do preço vem de profile.moeda (catálogo público
// também — PublicController serve guia.Moeda). MoedaPreco virou vestígio.

export default function RoteirosSiteSection() {
  const { C } = useTheme();
  const { profile } = useAuth();
  const moedaSimbolo = simboloMoeda(profile?.moeda ?? "EUR");
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(new Set()); // ids com mudança pendente
  const [saving, setSaving] = useState(null); // id sendo salvo

  async function reload() {
    setLoading(true);
    try {
      const data = await listRoteiros();
      setLista(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  function updateLocal(id, patch) {
    setLista((cur) => cur.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty((d) => new Set(d).add(id));
  }

  async function salvar(r) {
    setSaving(r.id);
    try {
      await updateRoteiroSite(r.id, {
        exibirNoSite: !!r.exibirNoSite,
        precoApartirDe: r.precoApartirDe ? Number(r.precoApartirDe) : null,
        // PR4: moedaPreco não vai mais no payload (servidor ignora; catálogo
        // público serve guia.Moeda).
        descricaoCurta: (r.descricaoCurta || "").trim() || null,
        descricaoCurtaIt: (r.descricaoCurtaIt || "").trim() || null,
        imagemUrl: (r.imagemUrl || "").trim() || null,
      });
      setDirty((d) => {
        const n = new Set(d);
        n.delete(r.id);
        return n;
      });
    } catch {
      alert("Erro ao salvar.");
    } finally {
      setSaving(null);
    }
  }

  const fieldStyle = {
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.inputBg,
    color: C.text,
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
    width: "100%",
  };

  return (
    <div
      style={{
        background: C.bgCard,
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        padding: 24,
        marginTop: 20,
        maxWidth: 820,
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: 0 }}>
          🗺️ Roteiros no site
        </h2>
        <p style={{ color: C.textMuted, fontSize: 12, margin: "4px 0 0" }}>
          Marque quais roteiros aparecem na página pública como catálogo.
          Campos de cliente/preço real nunca vazam.
        </p>
      </div>

      {loading ? (
        <div style={{ color: C.textMuted, fontSize: 12, padding: 12 }}>Carregando...</div>
      ) : lista.length === 0 ? (
        <div style={{ fontSize: 12, color: C.textMuted, fontStyle: "italic", padding: 12, textAlign: "center" }}>
          Nenhum roteiro cadastrado.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lista.map((r) => {
            const isDirty = dirty.has(r.id);
            const isSaving = saving === r.id;
            return (
              <div
                key={r.id}
                style={{
                  padding: 14,
                  background: C.bg,
                  borderRadius: 10,
                  border: `1px solid ${r.exibirNoSite ? `${C.accent}40` : C.border}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.titulo}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                      {r.clienteNome ?? "(sem cliente)"} · {r.dataInicio} → {r.dataFim}
                    </div>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", whiteSpace: "nowrap" }}>
                    <input
                      type="checkbox"
                      checked={!!r.exibirNoSite}
                      onChange={(e) => updateLocal(r.id, { exibirNoSite: e.target.checked })}
                    />
                    <span style={{ fontSize: 12, color: r.exibirNoSite ? C.green : C.textMuted, fontWeight: 600 }}>
                      Exibir no site
                    </span>
                  </label>
                </div>

                {r.exibirNoSite && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.textMuted, minWidth: 24 }}>
                        {moedaSimbolo}
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Preço a partir de"
                        value={r.precoApartirDe ?? ""}
                        onChange={(e) => updateLocal(r.id, { precoApartirDe: e.target.value })}
                        style={{ ...fieldStyle, width: 140, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 11, color: C.textDim, fontStyle: "italic" }}>
                        moeda do seu perfil
                      </span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <ImageUploadField
                        label="Imagem do roteiro"
                        hint="800×600 · recortada em 4:3"
                        shape="rect-4x3"
                        width={200}
                        height={150}
                        value={r.imagemUrl || null}
                        onUpload={async (file) => {
                          const url = await uploadRoteiroImagem(r.id, file);
                          // Atualiza in-place sem marcar como dirty — o upload já
                          // persistiu a URL no banco.
                          setLista((cur) => cur.map((x) => (x.id === r.id ? { ...x, imagemUrl: url } : x)));
                        }}
                        onDelete={async () => {
                          await deleteRoteiroImagem(r.id);
                          setLista((cur) => cur.map((x) => (x.id === r.id ? { ...x, imagemUrl: null } : x)));
                        }}
                      />
                    </div>
                  </>
                )}

                {r.exibirNoSite && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <textarea
                      rows={2}
                      placeholder="Descrição curta (PT) — ~30-40 palavras"
                      value={r.descricaoCurta ?? ""}
                      onChange={(e) => updateLocal(r.id, { descricaoCurta: e.target.value })}
                      maxLength={300}
                      style={{ ...fieldStyle, resize: "vertical" }}
                    />
                    <textarea
                      rows={2}
                      placeholder="Descrição curta (IT)"
                      value={r.descricaoCurtaIt ?? ""}
                      onChange={(e) => updateLocal(r.id, { descricaoCurtaIt: e.target.value })}
                      maxLength={300}
                      style={{ ...fieldStyle, resize: "vertical" }}
                    />
                  </div>
                )}

                {isDirty && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={() => salvar(r)}
                      disabled={isSaving}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 8,
                        border: "none",
                        background: C.accent,
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: isSaving ? "wait" : "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        opacity: isSaving ? 0.6 : 1,
                      }}
                    >
                      {isSaving ? "Salvando..." : "Salvar alterações"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
